import _styled from "styled-components";
const _excluded = ["children", "className", "style", "open", "height", "width", "container", "hideOverlay", "onClose", "onAnimationStart", "onAnimationComplete", "overlayStyle", "shouldCloseOnEsc", "shouldCloseOnOverlayClick", "size", "initial", "animate", "exit"];
function _extends() { return _extends = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends.apply(null, arguments); }
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
function _objectWithoutProperties(e, t) { if (null == e) return {}; var o, r, i = _objectWithoutPropertiesLoose(e, t); if (Object.getOwnPropertySymbols) { var s = Object.getOwnPropertySymbols(e); for (r = 0; r < s.length; r++) o = s[r], t.includes(o) || {}.propertyIsEnumerable.call(e, o) && (i[o] = e[o]); } return i; }
function _objectWithoutPropertiesLoose(r, e) { if (null == r) return {}; var t = {}; for (var n in r) if ({}.hasOwnProperty.call(r, n)) { if (e.includes(n)) continue; t[n] = r[n]; } return t; }
import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { AnimatePresence } from '@adsk/alloy-react-animation';
import { useForwardedRef, variantSwitch } from '@adsk/alloy-react-helpers';
import Portal from '@adsk/alloy-react-portal';
import theme, { stylePropType } from '@adsk/alloy-react-theme';
import { Overlay, Header, Body, Footer, Close, PrevArrow, NextArrow } from './layout';
import ModalContext from './ModalContext';
const SIZES = {
  SMALL: 'small',
  MEDIUM: 'medium',
  LARGE: 'large',
  EXTRA_LARGE: 'extra_large'
};
/**
 * A modal is an overlay that focuses the customer's attention on a single task or set of controls.
 * It creates a mode that disables the main window but keeps it visible with the modal window as a child window in front of it.
 * Users must interact with the modal window before they can return to the parent application.
 */
const Modal = /*#__PURE__*/React.forwardRef((_ref, ref) => {
  let {
      children,
      className,
      style,
      open,
      height,
      width,
      container = document.body,
      hideOverlay,
      onClose,
      onAnimationStart,
      onAnimationComplete,
      overlayStyle,
      shouldCloseOnEsc = true,
      shouldCloseOnOverlayClick = true,
      size = SIZES.MEDIUM,
      initial,
      animate,
      exit
    } = _ref,
    props = _objectWithoutProperties(_ref, _excluded);
  const modalRef = useForwardedRef(ref);
  const onClick = useCallback(e => {
    if (modalRef.current.contains(e.target)) {
      // Don't close the modal if the click is inside the modal
      return;
    }
    shouldCloseOnOverlayClick && (onClose === null || onClose === void 0 ? void 0 : onClose(e));
  }, [modalRef, shouldCloseOnOverlayClick, onClose]);
  const onEscape = useCallback(e => {
    onClose === null || onClose === void 0 ? void 0 : onClose();
    e.stopPropagation();
  }, [onClose]);
  const context = useMemo(() => ({
    shouldCloseOnEsc
  }), [shouldCloseOnEsc]);
  return /*#__PURE__*/React.createElement(Portal, {
    container: container
  }, /*#__PURE__*/React.createElement(AnimatePresence, null, open && /*#__PURE__*/React.createElement(Overlay, {
    "data-testid": "modal-overlay",
    hide: hideOverlay,
    onClick: onClick,
    onAnimationStart: onAnimationStart,
    onAnimationComplete: onAnimationComplete,
    initial: initial,
    animate: animate,
    exit: exit,
    style: overlayStyle,
    onEscape: shouldCloseOnEsc ? onEscape : undefined
  }, /*#__PURE__*/React.createElement(_StyledDiv, _extends({
    tabIndex: -1
  }, props, {
    className: className,
    ref: modalRef,
    $_css: [_objectSpread({
      position: 'relative',
      display: 'flex',
      flexDirection: 'column',
      maxWidth: 'calc(100vw - 32px)',
      width: width || variantSwitch({
        statement: size,
        variants: {
          [SIZES.SMALL]: 420,
          [SIZES.MEDIUM]: 600,
          [SIZES.LARGE]: 800,
          [SIZES.EXTRA_LARGE]: 1140
        }
      }) || undefined,
      maxHeight: 'calc(100vh - 120px)',
      height: height || variantSwitch({
        statement: size,
        variants: {
          [SIZES.SMALL]: 359,
          [SIZES.MEDIUM]: 480,
          [SIZES.LARGE]: 644,
          [SIZES.EXTRA_LARGE]: 733
        }
      }) || undefined,
      outline: 'none',
      backgroundColor: theme.tokens.colors.background.primary.value,
      borderRadius: theme.borderRadius.medium,
      boxShadow: 'rgba(0, 0, 0, 0.25) 0px 0px 8px 0px',
      zIndex: theme.zIndex.modal
    }, hideOverlay && {
      pointerEvents: 'all'
    }), style]
  }), /*#__PURE__*/React.createElement(ModalContext.Provider, {
    value: context
  }, children)))));
});
Modal.displayName = 'Modal';
Modal.propTypes = {
  /** The content of modal */
  children: PropTypes.any,
  /** Styles applied to the root element */
  style: stylePropType,
  /** Class applied to the root element */
  className: PropTypes.string,
  /** is modal open */
  open: PropTypes.bool,
  /** modal width, default is 400px */
  width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  /** modal height, default is 400px */
  height: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  /** Container Used for attaching the modal as a child component to the container */
  container: PropTypes.object,
  /** hide modal Overlay */
  hideOverlay: PropTypes.bool,
  /** called on click close modal */
  onClose: PropTypes.func,
  /** A callback that's called when the modal has started open animation */
  onAnimationStart: PropTypes.func,
  /** A callback that's called when the modal has Complete Animation close animation */
  onAnimationComplete: PropTypes.func,
  /** Set whether modal should be closed when pressing on esc button */
  shouldCloseOnEsc: PropTypes.bool,
  /** Set whether modal should be closed when click on overlay */
  shouldCloseOnOverlayClick: PropTypes.bool,
  /** Modal size */
  size: PropTypes.oneOf(Object.values(SIZES))
};
export default Object.assign(Modal, {
  Overlay,
  Header,
  Body,
  Footer,
  Close,
  PrevArrow,
  NextArrow,
  SIZES
});
var _StyledDiv = _styled("div").withConfig({
  displayName: "Modal___StyledDiv",
  componentId: "sc-ilcqvo-0"
})(["", ""], p => p.$_css);
//# sourceMappingURL=Modal.js.map